import type { ReactElement, ReactNode } from 'react'
import { Card, CardHead } from '~/components/ui/Card'
import { SegmentedControl } from '~/components/ui/Controls'

export interface LegendKey {
  label: string
  color?: string
  shape: 'line' | 'area' | 'hatch'
  title?: string
}

const VIEWS = [
  { id: 'chart', label: 'Chart' },
  { id: 'table', label: 'Table' },
]

/**
 * A plot, the sentence that says how to read it, its key, and the choice of
 * seeing the same figures as rows instead.
 *
 * The choice is a segmented control rather than tabs: the underlined tab is the
 * band under the app bar, where it says which page is being read, and repeating
 * it inside a card makes two kinds of navigation out of one change of state.
 */
export function ChartCard({
  title,
  description,
  view,
  onViewChange,
  legend,
  children,
}: {
  title: string
  description: string
  view: 'chart' | 'table'
  onViewChange(view: 'chart' | 'table'): void
  legend: readonly LegendKey[]
  children: ReactNode
}): ReactElement {
  return (
    <Card>
      <CardHead
        title={title}
        lede={description}
        aside={
          <SegmentedControl
            label={`${title} view`}
            choices={VIEWS}
            value={view}
            onChange={(id) => onViewChange(id === 'table' ? 'table' : 'chart')}
          />
        }
      />

      {legend.length > 0 && (
        <ul className="nw-legend">
          {legend.map((key) => (
            <li key={key.label} title={key.title}>
              <Key entry={key} />
              {key.label}
            </li>
          ))}
        </ul>
      )}

      {children}
    </Card>
  )
}

function Key({ entry }: { entry: LegendKey }): ReactElement {
  if (entry.shape === 'hatch') return <span className="nw-hatch-key" style={{ color: entry.color }} />
  return (
    <span
      className={entry.shape === 'line' ? 'nw-key nw-key--line' : 'nw-key'}
      style={{ background: entry.color }}
    />
  )
}
